// btw-front/src/components/admin/dashboard/CompanyDatasetCard.jsx
// 고객사 검색 데이터셋 상태 카드 — [통계] 탭 하단
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { EmptyState } from "../../ui/EmptyState";
import * as adminCompanyDataset from "../../../api/adminCompanyDataset";
import { formatKRW } from "./shared";

// 고객사 검색 데이터셋 상태 카드 — 활성 월/사업장 수/자동 갱신 시각 표시 + [지금 갱신].
// 데이터셋은 전역 단일 포인터라 모든 파트너·관리자가 같은 최신월을 참조(시점별 결과 불일치 방지).
export default function CompanyDatasetCard() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setLoading(true);
    adminCompanyDataset
      .getStatus()
      .then((res) => setStatus(res))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  // YYYYMM(int) → "2026년 6월"
  const fmtMonth = (ym) => {
    if (!ym) return "-";
    const y = Math.floor(ym / 100);
    const m = ym % 100;
    return t("admin.stats.datasetMonthValue", { year: y, month: m });
  };
  const fmtDate = (v) => (v ? String(v).slice(0, 10) : t("admin.stats.datasetNever"));

  const onRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    adminCompanyDataset
      .refresh()
      .then((r) => {
        if (r.changed) {
          message.success(t("admin.stats.datasetRefreshDone", { month: fmtMonth(r.latestYm) }));
        } else if (r.reason === "resolve-failed" || r.reason === "probe-failed") {
          // 데이터 제공처(odcloud) 확인 실패 — "최신"으로 오해시키지 않도록 경고로 구분
          message.warning(t("admin.stats.datasetRefreshUnavailable"));
        } else if (r.reason === "sparse" || r.reason === "empty-candidate") {
          // 신규월이 아직 덜 채워져 보류
          message.info(t("admin.stats.datasetRefreshSparse"));
        } else {
          // not-newer 등 — 이미 최신
          message.info(t("admin.stats.datasetRefreshKept"));
        }
        load();
      })
      .catch(() => message.error(t("admin.stats.datasetRefreshFailed")))
      .finally(() => setRefreshing(false));
  };

  if (loading) {
    return <p className="py-6 text-center text-sm text-slate-400">{t("common.loading")}</p>;
  }
  if (!status) {
    return <EmptyState title={t("admin.empty")} />;
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-slate-500">{t("admin.stats.datasetMonth")}</dt>
            <dd className="mt-0.5 font-semibold text-slate-900">{fmtMonth(status.yearMonth)}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">{t("admin.stats.datasetRowCount")}</dt>
            <dd className="mt-0.5 font-medium tabular-nums text-slate-900">
              {status.rowCount != null
                ? `${formatKRW(status.rowCount)} ${t("common.unitCount")}`
                : "-"}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">{t("admin.stats.datasetAutoAt")}</dt>
            <dd className="mt-0.5 font-medium text-slate-900">{status.refreshAt || "-"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">{t("admin.stats.datasetLastChecked")}</dt>
            <dd className="mt-0.5 font-medium text-slate-900">{fmtDate(status.lastCheckedAt)}</dd>
          </div>
        </dl>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="btw-btn-ghost shrink-0 disabled:opacity-60"
        >
          {refreshing ? t("admin.stats.datasetRefreshing") : t("admin.stats.datasetRefresh")}
        </button>
      </div>
      {status.pinned && (
        <p className="mt-3 text-xs text-amber-600">{t("admin.stats.datasetPinned")}</p>
      )}
    </div>
  );
}
